import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Meme } from '@/types';

interface MemeDetectorProps {
  meme: Meme;
  children: React.ReactNode;
}

export function MemeDetector({ meme, children }: MemeDetectorProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLSpanElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLSpanElement>) => {
    // Используем глобальные координаты для fixed позиционирования
    setPosition({
      x: e.clientX,
      y: e.clientY,
    });
  };

  useEffect(() => {
    if (!isHovered || !tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = position.x + 20;
    let top = position.y + 10;

    // Проверка правой границы
    if (left + tooltipRect.width > viewportWidth) {
      left = position.x - tooltipRect.width - 20;
    }

    // Проверка левой границы
    if (left < 0) {
      left = 10;
    }

    // Проверка нижней границы
    if (top + tooltipRect.height > viewportHeight) {
      top = position.y - tooltipRect.height - 10;
    }

    // Проверка верхней границы
    if (top < 0) {
      top = 10;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }, [isHovered, position]);

  return (
    <span
      ref={containerRef}
      className="relative inline-block cursor-help"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={handleMouseMove}
    >
      {children}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            ref={tooltipRef}
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ duration: 0.2 }}
            className="fixed z-50 w-auto p-4 bg-[#202020] border border-[#2F2F2F] rounded-sm shadow-xl pointer-events-none"
            style={{
              left: position.x + 20,
              top: position.y + 10,
              maxWidth: 'min(calc(100vw - 2rem), 400px)',
              minWidth: '280px',
            }}
          >
            <h4 className="text-white/90 font-semibold mb-2 break-words">{meme.title}</h4>
            <div className="space-y-1">
              <span className="text-[#9B9A97] text-xs font-medium block">Пояснение к мему:</span>
              <p className="text-[#9B9A97] text-sm break-words">{meme.description}</p>
            </div>
            {meme.gif && (
              <img src={meme.gif} alt={meme.title} className="mt-3 rounded-sm max-w-full" />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}
